// ====== FULL FILE: src/components/ProofOfLife.js ======
// Manager-aware, read-only friendly. Keeps your look & feel.
// - Managers see a "View" button per tile (no Edit/Delete/Add).
// - View modal summarizes fields + shows media as a thumbnail; videos are still playable.
// - Buttons safe in read-only carry data-allow-readonly.

import React, { useEffect, useRef, useState } from "react";
import { auth, db, storage } from "../firebase";
import { useFreeLimits } from "../hooks/useFreeLimits";
import UpgradeModal from "./UpgradeModal";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref as sRef,
  uploadBytesResumable,
} from "firebase/storage";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import "../styles/layout.css";
import { useAccess } from "../access/AccessContext"; // readOnly scope
import VideoLightbox from "./ui/VideoLightbox"; // shared lightbox

function defaultForm(uid) {
  return {
    uid,
    type: "text", // text | photo | video | audio
    note: "",
    mediaUrl: "",
    storagePath: null,
    convertedPath: null,
    thumbPath: null,
    convertProgress: null,
    file: null,
  };
}

// ---------- helpers ----------
const isHttp = (u) => typeof u === "string" && /^https?:\/\//i.test(u);
function toStoragePath(maybe) {
  if (!maybe || typeof maybe !== "string") return null;
  if (maybe.startsWith("gs://")) {
    const no = maybe.replace(/^gs:\/\//, "");
    const slash = no.indexOf("/");
    return slash >= 0 ? no.slice(slash + 1) : null;
  }
  if (maybe.startsWith("http")) {
    const m = maybe.match(/\/o\/([^?]+)/);
    if (!m) return null;
    try { return decodeURIComponent(m[1]); } catch { return m[1]; }
  }
  if (maybe.includes("..")) return null;
  return maybe;
}
async function toHttpsUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (isHttp(pathOrUrl)) return pathOrUrl;
  const p = toStoragePath(pathOrUrl) || pathOrUrl;
  try { return await getDownloadURL(sRef(storage, p)); } catch { return null; }
}
const extFromPath = (p = "") => (String(p).toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] || "");
const extOf = (u = "") => (String(u).toLowerCase().match(/\.([a-z0-9]+)(?:$|\?)/)?.[1] || "");

function toJsDate(value) {
  if (!value) return null;
  try {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === "number" || typeof value === "string") {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof value === "object" && typeof value.toDate === "function") {
      const d = value.toDate();
      return Number.isNaN(d.getTime()) ? null : d;
    }
  } catch {}
  return null;
}

function formatDateTime(value) {
  const date = toJsDate(value);
  if (!date) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} · ${hours}:${minutes}`;
}
const CREATED_KEYS = ["created", "createdAt", "created_at", "createdDate", "createdOn"];
const UPDATED_KEYS = ["updated", "updatedAt", "updated_at", "updatedDate", "updatedOn"];

function resolveTimestamp(entry, keys) {
  if (!entry) return null;
  for (const key of keys) {
    const date = toJsDate(entry[key]);
    if (date) return date;
  }
  return null;
}

function getCreatedDate(entry) {
  return resolveTimestamp(entry, CREATED_KEYS) || resolveTimestamp(entry, UPDATED_KEYS);
}

function getUpdatedDate(entry) {
  return resolveTimestamp(entry, UPDATED_KEYS);
}


const VIDEO_SUPPORT = (() => {
  if (typeof document === "undefined") return { mp4: true, webm: true };
  const v = document.createElement("video");
  const can = (t) => (v && v.canPlayType ? v.canPlayType(t) !== "" : true);
  return {
    mp4: can('video/mp4; codecs="avc1.42E01E, mp4a.40.2"') || can("video/mp4"),
    webm: can('video/webm; codecs="vp9, opus"') || can("video/webm"),
  };
})();
const AUDIO_SUPPORT = (() => {
  if (typeof document === "undefined") return { mp3: true, webm: true, m4a: true };
  const a = document.createElement("audio");
  const can = (t) => (a && a.canPlayType ? a.canPlayType(t) !== "" : true);
  return {
    mp3: can("audio/mpeg"),
    webm: can('audio/webm; codecs="opus"') || can("audio/webm"),
    m4a: can("audio/mp4") || can("audio/aac"),
  };
})();

function pickMedia(entry) {
  const type =
    entry?.type ||
    (entry?.videoUrl ? "video" : entry?.imageUrl ? "photo" : entry?.audioUrl ? "audio" : "text");
  const mediaUrl =
    entry?.mediaUrl || entry?.videoUrl || entry?.imageUrl || entry?.audioUrl || entry?.url || "";
  const storagePath = entry?.storagePath || entry?.path || null;
  const convertedPath = entry?.convertedPath || null;
  const thumbPath = entry?.thumbPath || entry?.posterUrl || null;
  const convertProgress = typeof entry?.convertProgress === "number" ? entry.convertProgress : null;
  return { type, mediaUrl, storagePath, convertedPath, thumbPath, convertProgress };
}

function useResolvedMedia(entry) {
  const [src, setSrc] = useState(null);
  const [poster, setPoster] = useState(null);
  const dep = { t: entry?.type, u: entry?.mediaUrl, cp: entry?.convertedPath, tp: entry?.thumbPath };
  useEffect(() => {
    let alive = true;
    (async () => {
      const { type, mediaUrl, convertedPath, thumbPath } = pickMedia(entry || {});
      if (type === "video") {
        const play = convertedPath || mediaUrl || null;
        const post = thumbPath || null;
        const [sUrl, pUrl] = await Promise.all([toHttpsUrl(play), toHttpsUrl(post)]);
        if (!alive) return;
        setSrc(sUrl || null);
        setPoster(pUrl || null);
      } else if (type === "photo") {
        const p = await toHttpsUrl(mediaUrl);
        if (!alive) return;
        setSrc(p); setPoster(p);
      } else if (type === "audio") {
        const a = await toHttpsUrl(mediaUrl);
        if (!alive) return;
        setSrc(a); setPoster(null);
      } else { if (!alive) return; setSrc(null); setPoster(null); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep.t, dep.u, dep.cp, dep.tp]);
  return { src, poster };
}

// Pick best MediaRecorder mime
function pickRecorder(type) {
  const sup = (m) =>
    window.MediaRecorder && window.MediaRecorder.isTypeSupported
      ? window.MediaRecorder.isTypeSupported(m)
      : false;
  if (type === "video") {
    const candidates = [
      'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
      "video/mp4",
      'video/webm;codecs="vp9,opus"',
      "video/webm",
    ];
    const mime = candidates.find(sup) || "";
    const ext = mime.startsWith("video/mp4") ? "mp4" : mime.startsWith("video/webm") ? "webm" : "webm";
    return { mime: mime || undefined, ext };
  }
  const candidates = ["audio/mp4", "audio/aac", "audio/mpeg", 'audio/webm;codecs="opus"', "audio/webm"];
  const mime = candidates.find(sup) || "";
  const ext =
    mime.startsWith("audio/mpeg") ? "mp3" :
    (mime.startsWith("audio/mp4") || mime.startsWith("audio/aac")) ? "m4a" :
    mime.includes("webm") ? "webm" : "webm";
  return { mime: mime || undefined, ext };
}
function canRecord(kind) {
  if (!navigator?.mediaDevices?.getUserMedia || !window.MediaRecorder) return false;
  const { mime } = pickRecorder(kind);
  if (kind === "video" && !mime) return false;
  return true;
}
function isPlayableVideoEXT(ext) {
  const ex = (ext || "").toLowerCase();
  if (["mp4", "m4v", "mov"].includes(ex)) return VIDEO_SUPPORT.mp4;
  if (ex === "webm") return VIDEO_SUPPORT.webm;
  return true;
}
function isPlayableAudioEXT(ext) {
  const ex = (ext || "").toLowerCase();
  if (ex === "mp3") return AUDIO_SUPPORT.mp3;
  if (ex === "m4a") return AUDIO_SUPPORT.m4a;
  if (ex === "webm") return AUDIO_SUPPORT.webm;
  return true;
}
// REMOVED unused helper:
// const _fmtDate = (d) => { try { const x = typeof d === "string" ? new Date(d) : d?.toDate ? d.toDate() : new Date(d); return isNaN(x.getTime()) ? "—" : x.toLocaleString(); } catch { return "—"; } };
function shallowEqualProofs(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i], y = b[i];
    if (x.id !== y.id) return false;
    if ((x.updated?.seconds || 0) !== (y.updated?.seconds || 0)) return false;
    if ((x.created?.seconds || 0) !== (y.created?.seconds || 0)) return false;
    if ((x.mediaUrl || "") !== (y.mediaUrl || "")) return false;
    if ((x.convertedPath || "") !== (y.convertedPath || "")) return false;
    if ((x.thumbPath || "") !== (y.thumbPath || "")) return false;
    if ((x.convertProgress ?? null) !== (y.convertProgress ?? null)) return false;
    if ((x.type || "") !== (y.type || "")) return false;
    if ((x.note || "") !== (y.note || "")) return false;
  }
  return true;
}

export default function ProofOfLife({ user }) {
  const { readOnly } = useAccess() || { readOnly: false };

  const [proofs, setProofs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const { free } = useFreeLimits(user);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [form, setForm] = useState(defaultForm(user?.uid));
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recording, setRecording] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordType, setRecordType] = useState(null);
  const [fileUrl, setFileUrl] = useState("");
  const [editingMeta, setEditingMeta] = useState({ created: null, updated: null });

  // Manager viewer modal
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerEntry, setViewerEntry] = useState(null);

  // Resolved preview for manager modal (top-level hook)
  const { src: viewerSrc, poster: viewerPoster } = useResolvedMedia(viewerEntry || {});

  const fileInput = useRef();
  const videoRef = useRef();

  // live list (debounced)
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "proofOfLife"), where("uid", "==", user.uid), orderBy("created", "desc"));
    return onSnapshot(q, (snap) => {
      const next = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setProofs((prev) => (shallowEqualProofs(prev, next) ? prev : next));
    });
  }, [user?.uid]);

  // object URL for new local file
  useEffect(() => {
    if (!form.file) { setFileUrl(""); return; }
    const url = URL.createObjectURL(form.file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.file]);

  // live camera preview when recording
  useEffect(() => {
    const el = videoRef.current;
    if (el && recording && mediaStream && recordType === "video") {
      el.srcObject = mediaStream;
    }
    return () => { if (el) el.srcObject = null; };
  }, [recording, mediaStream, recordType]);

  function openForm(proof = null) {
    if (!proof && free && proofs.length >= 1) { setShowUpgrade(true); return; }
    if (readOnly) return; // managers cannot edit
    stopRecording();
    setUploading(false);
    setUploadProgress(0);
    if (proof) {
      const m = pickMedia(proof);
      const createdAt = getCreatedDate(proof);
      const updatedAt = getUpdatedDate(proof) || createdAt;
      setForm({
        uid: user.uid,
        type: m.type,
        note: proof.note || "",
        mediaUrl: m.mediaUrl || "",
        storagePath: m.storagePath || null,
        convertedPath: m.convertedPath || null,
        thumbPath: m.thumbPath || null,
        convertProgress: typeof proof.convertProgress === "number" ? proof.convertProgress : null,
        file: null,
      });
      setEditingId(proof.id);
      setEditingMeta({ created: createdAt, updated: updatedAt });
    } else {
      setForm(defaultForm(user.uid));
      setEditingId(null);
      setEditingMeta({ created: null, updated: null });
    }
    setShowForm(true);
    if (fileInput.current) fileInput.current.value = "";
  }
  function closeForm() {
    setShowForm(false);
    setForm(defaultForm(user.uid));
    setEditingId(null);
    setUploading(false);
    setUploadProgress(0);
    stopRecording();
    setEditingMeta({ created: null, updated: null });
  }

  function stopRecording() {
    try { mediaRecorder?.stop(); } catch {}
    try { mediaStream?.getTracks().forEach((t) => t.stop()); } catch {}
    setRecording(false);
    setMediaStream(null);
    setMediaRecorder(null);
    setRecordType(null);
  }

  function handleInput(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (name === "type") {
      setForm((f) => ({ ...f, file: null, mediaUrl: "", storagePath: null, convertedPath: null, thumbPath: null, convertProgress: null }));
      setFileUrl("");
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  function handleFile(e) {
    const f = e.target.files?.[0];
    if (f) setForm((frm) => ({ ...frm, file: f }));
  }

  async function startRecording(type) {
    setRecordType(type);
    try {
      const constraints = type === "video" ? { video: true, audio: true } : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setMediaStream(stream);
      const { mime, ext } = pickRecorder(type);
      if (type === "video" && !mime) throw new Error("Recording not supported on this device/browser");
      const opts = mime ? { mimeType: mime } : {};
      const mr = new MediaRecorder(stream, opts);

      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: mime || (type === "video" ? "video/webm" : "audio/webm") });
        const newFile = new File([blob], `proof.${type}.${ext}`, { type: blob.type });
        setForm((f) => ({ ...f, file: newFile }));
        setFileUrl(URL.createObjectURL(blob));
        stopRecording();
      };

      mr.start();
      setMediaRecorder(mr);
      setRecording(true);
    } catch (err) {
      alert("Recording error: " + (err?.message || String(err)));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (readOnly) return; // guard
    setUploading(true);
    setUploadProgress(0);
    try {
      let { mediaUrl, storagePath, convertedPath, thumbPath } = form;
      let convertProgress = typeof form.convertProgress === "number" ? form.convertProgress : null;

      if (["photo", "video", "audio"].includes(form.type) && form.file) {
        const rawExt = form.file.name.split(".").pop()?.toLowerCase();
        const ext = rawExt || (form.type === "photo" ? "jpg" : form.type === "video" ? "webm" : "webm");
        const filename = `${user.uid}_${Date.now()}.${ext}`;
        const ref = sRef(storage, `proofOfLife/${user.uid}/${filename}`);
        const task = uploadBytesResumable(ref, form.file);
        task.on("state_changed", (snap) => {
          if (snap.totalBytes) setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
        });
        await task;
        mediaUrl = await getDownloadURL(task.snapshot.ref);
        storagePath = task.snapshot.ref.fullPath;
        convertedPath = null;
        thumbPath = null;
        convertProgress = 0;
      }

      const base = {
        uid: user.uid,
        type: form.type,
        note: form.note,
        mediaUrl: mediaUrl || "",
        storagePath: storagePath || null,
        convertedPath: convertedPath || null,
        thumbPath: thumbPath || null,
        convertProgress: convertProgress ?? null,
        updated: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(db, "proofOfLife", editingId), base);
      } else {
        await addDoc(collection(db, "proofOfLife"), { ...base, created: serverTimestamp() });
      }

      closeForm();
    } catch (err) {
      alert("Save failed: " + err.message);
      setUploading(false);
    }
  }

  async function handleDeleteCurrent() {
    if (readOnly) return;
    if (!editingId) return;
    if (!window.confirm("Delete this entry?")) return;
    try {
      const entry = proofs.find((p) => p.id === editingId);
      const { mediaUrl, convertedPath, thumbPath, storagePath } = pickMedia(entry || {});
      const raw = toStoragePath(mediaUrl) || storagePath;
      if (raw) await deleteObject(sRef(storage, raw)).catch(() => {});
      if (convertedPath) await deleteObject(sRef(storage, convertedPath)).catch(() => {});
      if (thumbPath) await deleteObject(sRef(storage, thumbPath)).catch(() => {});
      await deleteDoc(doc(db, "proofOfLife", editingId));
      closeForm();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  }

  function Progress({ value }) {
    if (typeof value !== "number") {
      return (
        <div className="upl-wrap" aria-live="polite">
          <div className="upl-bar">
            <div className="upl-fill" style={{ width: "40%", animation: "pulse 1.2s infinite" }} />
          </div>
          <div className="upl-text">Processing…</div>
          <style>{`@keyframes pulse{0%{opacity:.4;width:20%}50%{opacity:1;width:60%}100%{opacity:.4;width:20%}}`}</style>
        </div>
      );
    }
    return (
      <div className="upl-wrap" aria-live="polite">
        <div className="upl-bar">
          <div className="upl-fill" style={{ width: `${Math.max(1, Math.min(100, value))}%` }} />
        </div>
        <div className="upl-text">Processing… {Math.max(0, Math.min(100, Math.round(value)))}%</div>
      </div>
    );
  }

  // === manager-viewer helpers ===
  async function fetchSignedUrl(ownerUid, storagePath) {
    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Not signed in");
      const idToken = await u.getIdToken();
      const res = await fetch("/api/accountManagers/getDownloadUrl", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ ownerUid, storagePath }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data.error || "Failed to create link");
      return data.url;
    } catch (e) {
      alert(e.message || "Failed to create link");
      return null;
    }
  }
  async function handleDownload(storagePath) {
    const ownerUid = user?.uid;
    if (!ownerUid || !storagePath) return;
    const url = await fetchSignedUrl(ownerUid, storagePath);
    if (url) window.open(url, "_blank", "noopener");
  }
  async function handleForward(storagePath, noteText) {
    const ownerUid = user?.uid;
    if (!ownerUid || !storagePath) return;
    const url = await fetchSignedUrl(ownerUid, storagePath);
    if (!url) return;
    const subject = encodeURIComponent("Shared Proof of Life entry");
    const body = encodeURIComponent(`${noteText ? `Note: ${noteText}\n\n` : ""}${url}\n\nThis link expires soon.`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  function ProofRow({ proof, onView, readOnly }) {
    const { src, poster } = useResolvedMedia(proof);
    const [open, setOpen] = useState(false);
    const created = getCreatedDate(proof);

    const { type, mediaUrl, convertedPath, storagePath } = pickMedia(proof);

    const ex = extFromPath(storagePath) || extOf(mediaUrl || "");
    const playVideo = isPlayableVideoEXT(ex);
    const playAudio = isPlayableAudioEXT(ex);

    let canOpen = false;
    let processing = false;
    let incompatible = false;

    if (type === "video") {
      const isConverted = !!convertedPath;
      processing = !isConverted && !!mediaUrl && !["mp4", "m4v", "mov"].includes(ex.toLowerCase());
      incompatible = !processing && !playVideo;
      canOpen = !!src && playVideo && !processing;
    } else if (type === "audio") {
      incompatible = !playAudio;
      canOpen = !!src && playAudio;
    } else if (type === "photo") {
      canOpen = !!src;
    }

    const Thumb = () => {
      if (type === "photo") return poster ? <img src={poster} alt="" className="pol-thumb-img" /> : <span aria-hidden>🖼️</span>;
      if (type === "video") return poster ? <img src={poster} alt="" className="pol-thumb-img" /> : <span aria-hidden>🎬</span>;
      if (type === "audio") return <span aria-hidden>🎤</span>;
      return <span aria-hidden>✏️</span>;
    };

    const badge =
      type === "video"
        ? processing ? "Processing…" : (incompatible ? "Incompatible" : null)
        : (type === "audio" && incompatible ? "Incompatible" : null);
    const createdLabel = created ? formatDateTime(created) : "—";

    const noteText = (proof.note || "").trim();
    const hasNote = noteText.length > 0;

    const canToggle = canOpen || hasNote;
    const toggleOpen = () => {
      if (canToggle) setOpen((o) => !o);
    };
    const handleKeyToggle = (event) => {
      if (!canToggle) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setOpen((o) => !o);
      }
    };

    return (
      <div className={`card dv-item pol-row${open ? " is-open" : ""}`}>
        <div className="dv-left">
          <div
            className={`tile-thumb pol-thumb ${canToggle ? "is-clickable" : ""}`}
            onClick={toggleOpen}
            onKeyDown={handleKeyToggle}
            role={canToggle ? "button" : undefined}
            tabIndex={canToggle ? 0 : -1}
            title={
              canOpen
                ? open ? "Hide preview" : "Show preview"
                : canToggle
                  ? open ? "Hide details" : "Show details"
                  : badge || "No media"
            }
          >
            <Thumb />
            {badge && <span className="thumb-badge" aria-hidden>{badge}</span>}
          </div>
        </div>

        {/* FIXED: the next line was missing the `<` before div */}
        <div
          className={`dv-main${canToggle ? " is-clickable" : ""}`}
          onClick={toggleOpen}
          onKeyDown={handleKeyToggle}
          role={canToggle ? "button" : undefined}
          tabIndex={canToggle ? 0 : -1}
          title={
            canOpen
              ? open ? "Hide preview" : "Show preview"
              : canToggle
                ? open ? "Hide details" : "Show details"
                : createdLabel
          }
        >
          <div className="tile-title pol-date-label" title={createdLabel}>{createdLabel}</div>
        </div>

        <div className="dv-actions">
          {!readOnly ? (
            <button className="btn btn-main btn-edit" onClick={() => openForm(proof)}>Edit</button>
          ) : (
            <button
              data-allow-readonly
              className="btn btn-main btn-edit"
              onClick={() => onView(proof)}
              title="View details"
            >
              View
            </button>
          )}
        </div>

        {open && type === "video" && processing && (
          <div className="pol-inline"><Progress value={proof.convertProgress} /></div>
        )}
        {open && hasNote && (
          <div className="pol-inline pol-note-block">
            <div className="pol-note-heading">Note</div>
            <div className="pol-note-body">{noteText}</div>
          </div>
        )}

        {open && canOpen && (
          <div className="pol-inline">
            {type === "video" ? (
              <video
                src={src || undefined}
                poster={poster || undefined}
                controls
                playsInline
                preload="metadata"
                style={{ width: "100%", borderRadius: 8 }}
              />
            ) : type === "audio" ? (
              <audio src={src || undefined} controls preload="metadata" style={{ width: "100%" }} />
            ) : type === "photo" ? (
              <img src={src || undefined} alt="Preview" style={{ width: "100%", borderRadius: 8 }} />
            ) : null}
          </div>
        )}
      </div>
    );
  }

  const ActionsDesktop = () => (
    <div className="dp-help-row">
      <button data-allow-readonly type="button" className="btn dp-help read" onClick={() => setShowDrawer(true)}>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button data-allow-readonly type="button" className="btn dp-help see" onClick={() => setShowVideo(true)}>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );
  const ActionsMobile = ActionsDesktop;

  const { src: resolvedModalSrc, poster: resolvedModalPoster } = useResolvedMedia(form);

  // Generate a poster on-the-fly in Edit when none exists
  const [modalPoster, setModalPoster] = useState(null);
  useEffect(() => {
    const src = fileUrl || resolvedModalSrc || "";
    if (form.type !== "video" || !src || resolvedModalPoster) { setModalPoster(null); return; }
    let v; let alive = true;
    try {
      v = document.createElement("video");
      v.preload = "metadata"; v.muted = true; v.playsInline = true; v.src = src;
      v.onloadedmetadata = () => { try { v.currentTime = Math.min(0.25, (v.duration || 1) * 0.05); } catch {} };
      v.onseeked = () => {
        if (!alive) return;
        try {
          const c = document.createElement("canvas");
          c.width = v.videoWidth || 320; c.height = v.videoHeight || 180;
          c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
          setModalPoster(c.toDataURL("image/jpeg", 0.75));
        } catch {}
      };
    } catch {}
    return () => { alive = false; try { if (v) v.src = ""; } catch {} };
  }, [fileUrl, resolvedModalSrc, resolvedModalPoster, form.type]);

  const [showDrawer, setShowDrawer] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  // ---- UI (layout preserved) ----
  return (
    <div className="container">
      <div className="header-row">
        <h2>Proof of Life</h2>
        <div className="header-actions desktop-only"><ActionsDesktop /></div>
        {!readOnly && (
          <button className="btn-add" onClick={() => openForm()}>+</button>
        )}
      </div>

      <div className="hero-wrap"><img src="/lastwish-account-proof.jpg" alt="Proof of Life" className="hero-img" /></div>
      <div className="mobile-only" style={{ margin: "10px 0" }}><ActionsMobile /></div>

      <div className="dv-list pol-list">
        {proofs.length === 0 ? (
          <div className="empty-state">No proof entries yet.</div>
        ) : (
          proofs.map((p) => (
            <ProofRow
              key={p.id}
              proof={p}
              readOnly={readOnly}
              onView={(entry) => { setViewerEntry(entry); setViewerOpen(true); }}
            />
          ))
        )}
      </div>

      {/* === Manager "VIEW" modal (read-only) === */}
      {readOnly && viewerOpen && viewerEntry && (
        <div className="modal-bg" onClick={() => setViewerOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Entry details</h3>
            <div className="modal-body">
              <div className="pol-sum">
                {(() => {
                  const createdDate = getCreatedDate(viewerEntry);
                  const updatedDate = getUpdatedDate(viewerEntry);
                  const createdLabel = createdDate ? formatDateTime(createdDate) : "—";
                  const showUpdated = updatedDate && (!createdDate || updatedDate.getTime() !== createdDate.getTime());
                  return (
                    <>
                      <div><b>Date:</b> {createdLabel}</div>
                      {showUpdated && <div><b>Last updated:</b> {formatDateTime(updatedDate)}</div>}
                    </>
                  );
                })()}
                <div><b>Type:</b> {pickMedia(viewerEntry).type}</div>
              </div>

              <div style={{ margin: "6px 0 10px" }}>
                <b>Note:</b> <div>{viewerEntry.note || "—"}</div>
              </div>

              {(() => {
                const { type } = pickMedia(viewerEntry);
                return type !== "text" ? (
                  <div className="pol-preview" style={{ marginBottom: 10 }}>
                    {type === "photo" && <img src={viewerSrc || ""} alt="Preview" className="pol-modal-thumb" />}
                    {type === "video" && (
                      <video
                        src={viewerSrc || undefined}
                        poster={viewerPoster || undefined}
                        className="pol-modal-thumb"
                        controls
                        playsInline
                        preload="metadata"
                      />
                    )}
                    {type === "audio" && (
                      <audio src={viewerSrc || undefined} className="pol-audio" controls preload="metadata" />
                    )}
                  </div>
                ) : null;
              })()}

              <div className="form-actions" style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                <button
                  data-allow-readonly
                  type="button"
                  className="btn btn-main"
                  onClick={() => window.print()}
                >
                  Print
                </button>
                {(() => {
                  const { storagePath } = pickMedia(viewerEntry);
                  return storagePath ? (
                    <>
                      <button
                        data-allow-readonly
                        type="button"
                        className="btn btn-main"
                        onClick={() => handleDownload(storagePath)}
                      >
                        Download
                      </button>
                      <button
                        data-allow-readonly
                        type="button"
                        className="btn btn-main"
                        onClick={() => handleForward(storagePath, viewerEntry.note || "")}
                      >
                        Forward
                      </button>
                    </>
                  ) : null;
                })()}
                <button
                  data-allow-readonly
                  type="button"
                  className="btn btn-cancel"
                  onClick={() => setViewerOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === Owner edit modal (unchanged) === */}
      {!readOnly && showForm && (
        <div className="modal-bg" onClick={closeForm}>
          <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <h3>{editingId ? "Edit Proof" : "New Proof of Life"}</h3>
            <div className="modal-body">
              {editingId && (
                <div className="pol-meta-block">
                  <div>
                    <b>Created:</b> {editingMeta.created ? formatDateTime(editingMeta.created) : "—"}
                  </div>
                  {editingMeta.updated && (!editingMeta.created || editingMeta.updated.getTime() !== editingMeta.created.getTime()) && (
                    <div>
                      <b>Last updated:</b> {formatDateTime(editingMeta.updated)}
                    </div>
                  )}
                </div>
              )}
              <select name="type" className="input" value={form.type} onChange={handleInput} required>
                <option value="text">Text Only</option>
                <option value="photo">Photo</option>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
              </select>

              <textarea
                name="note"
                className="input"
                value={form.note}
                onChange={handleInput}
                placeholder="Your note (required)…"
                rows={3}
                required
              />

              {form.type === "video" && !recording && (
                <button
                  type="button"
                  className="btn btn-main"
                  onClick={() => startRecording("video")}
                  disabled={!canRecord("video")}
                  title={!canRecord("video") ? "Recording not supported in this browser/device" : undefined}
                >
                  🎥 Record Video
                </button>
              )}
              {form.type === "audio" && !recording && (
                <button
                  type="button"
                  className="btn btn-main"
                  onClick={() => startRecording("audio")}
                  disabled={!canRecord("audio")}
                  title={!canRecord("audio") ? "Recording not supported in this browser/device" : undefined}
                >
                  🎤 Record Audio
                </button>
              )}
              {recording && recordType === "video" && (
                <video ref={videoRef} autoPlay playsInline muted style={{ width: 220, margin: "12px 0", borderRadius: 6 }} />
              )}
              {recording && (
                <button type="button" className="btn btn-cancel" onClick={stopRecording}>
                  Stop Recording
                </button>
              )}

              {!recording && ["photo", "video", "audio"].includes(form.type) && (
                <input
                  type="file"
                  ref={fileInput}
                  className="input"
                  accept={form.type === "photo" ? "image/*" : form.type === "video" ? "video/*" : "audio/*"}
                  onChange={handleFile}
                  required={!form.mediaUrl && !form.file}
                />
              )}

              {(form.file || resolvedModalSrc) && !recording && form.type !== "text" && !uploading && (
                <div className="pol-preview">
                  {form.type === "photo" && (
                    <img src={fileUrl || resolvedModalSrc || ""} alt="Preview" className="pol-modal-thumb" />
                  )}
                  {form.type === "video" && (() => {
                    const ext =
                      (form.storagePath && (form.storagePath.match(/\.([a-z0-9]+)$/i)?.[1] || "")) ||
                      (form.mediaUrl && (form.mediaUrl.match(/\.([a-z0-9]+)(?:$|\?)/i)?.[1] || "")) ||
                      (form.file && (form.file.name.split(".").pop() || "")) || "";
                    return isPlayableVideoEXT(ext) ? (
                      <video
                        src={fileUrl || resolvedModalSrc || ""}
                        poster={resolvedModalPoster || modalPoster || undefined}
                        className="pol-modal-thumb"
                        controls
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <Progress value={form.convertProgress} />
                    );
                  })()}
                  {form.type === "audio" && isPlayableAudioEXT(
                    (form.storagePath && (form.storagePath.match(/\.([a-z0-9]+)$/i)?.[1] || "")) ||
                    (form.mediaUrl && (form.mediaUrl.match(/\.([a-z0-9]+)(?:$|\?)/i)?.[1] || "")) ||
                    (form.file && (form.file.name.split(".").pop() || "")) || ""
                  ) && (
                    <audio src={fileUrl || resolvedModalSrc || ""} className="pol-audio" controls preload="metadata" />
                  )}
                  {form.mediaUrl && !form.file && (
                    <div className="pol-open">
                      <a href={resolvedModalSrc || form.mediaUrl} target="_blank" rel="noopener noreferrer">
                        Open original
                      </a>
                    </div>
                  )}
                </div>
              )}

              {uploading && (
                <div className="upl-wrap" aria-live="polite">
                  <div className="upl-bar">
                    <div className="upl-fill" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <div className="upl-text">Uploading… {uploadProgress}%</div>
                </div>
              )}

              <div className="pol-action-rows">
                <div className="row bottom">
                  <button className="btn btn-main" type="submit" disabled={uploading}>
                    {editingId ? "Update" : "Add"}
                  </button>
                  {editingId && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleDeleteCurrent}
                      disabled={uploading}
                    >
                      Delete
                    </button>
                  )}
                  <button type="button" className="btn btn-cancel" onClick={closeForm}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            <style>{`
              .upl-wrap{margin-top:10px}
              .upl-bar{height:8px;background:#f0f2f8;border-radius:999px;overflow:hidden}
              .upl-fill{height:100%;background:#003353}
              .upl-text{font-size:12px;color:#657899;margin-top:6px}
              .pol-action-rows{ display:flex; flex-direction:column; gap:10px; margin-top:8px; }
              .pol-action-rows .row{ display:flex; gap:10px; flex-wrap:wrap; }
              @media (max-width:699px){ .pol-action-rows .row{ flex-direction:column; } .pol-action-rows .row .btn{ width:100%; } }
              .pol-preview{ display:grid; gap:8px; margin-top:8px; }
              .pol-modal-thumb{ width:100%; max-width:420px; border-radius:10px; background:#f4f5f9; }
              .pol-audio{ width:100%; max-width:420px; }
              .pol-open a{ color:#003353; font-weight:700; text-decoration:none; }
            `}</style>
          </form>
        </div>
      )}

      {/* Video help — URL updated per your request */}
      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/WADU-DSvQy8?si=5pvM0faMmw4wXkOP"
        title="How the Proof of Life page works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How the Proof of Life page works</div>
              <div className="dp-drawer-tools">
                <button data-allow-readonly className="btn btn-main" onClick={() => window.print()} type="button">Print</button>
                <button data-allow-readonly className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button">Close</button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p>Create periodic entries that prove you’re safe and well. Each entry can be text, a photo, a quick video, or an audio recording.</p>
              <p>New uploads may show <b>Processing…</b> if they require conversion; you’ll be able to play them as soon as processing completes.</p>
            </div>
          </aside>
        </div>
      )}

      <style>{`
        .header-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
        .header-actions{display:flex;align-items:center;gap:10px}
        .hero-wrap{margin:6px 0 14px}
        .hero-img{width:100%;height:130px;object-fit:cover;border-radius:14px}
        @media (max-width: 700px){
          .hero-img{height:110px}
        }
        .desktop-only{display:none}.mobile-only{display:block}
        @media(min-width:900px){.desktop-only{display:block}.mobile-only{display:none}}
        .dp-help-row{display:flex;gap:10px;flex-wrap:wrap}
        .dp-help{border-radius:8px;padding:10px 14px;display:inline-flex;align-items:center;gap:10px;font-weight:700;color:#fff;border:none}
        .dp-help.read{background:#650202}.dp-help.see{background:#003353}
        .dp-help-ico{font-size:1.05rem}
        .dp-video{position:relative;width:100%;padding-top:56.25%;border-radius:10px;overflow:hidden;background:#000;margin-bottom:12px}
        .dp-video iframe{position:absolute;inset:0;width:100%;height:100%}
        .dp-drawer-bg{position:fixed;inset:0;background:rgba(0,0,0,0.3);z-index:10000;display:flex;justify-content:flex-end}
        .dp-drawer{position:fixed;top:56px;right:0;height:calc(100vh - 56px);width:min(520px,95vw);background:#fff;box-shadow:-8px 0 24px rgba(0,0,0,0.15);border-top-left-radius:12px;border-bottom-left-radius:12px;display:flex;flex-direction:column;overflow:hidden}
        .dp-drawer-head{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:14px 16px;border-bottom:1px solid #f0f0f0;background:#fafafa}
        .dp-drawer-title{font-weight:700;color:#2a0516}
        .dp-drawer-tools{display:flex;gap:8px}
        .dp-drawer-body{padding:16px;overflow:auto}

        .pol-list{display:grid;grid-template-columns:1fr;gap:16px}
        .pol-row{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:12px;padding:12px}
        .pol-row .dv-main{min-width:0}
        .pol-row .dv-main .tile-title{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .pol-row .dv-main.is-clickable{cursor:pointer}
        .pol-row .pol-date-label{font-size:0.95rem;font-weight:600;color:#2a0516}
        .pol-row .dv-actions{display:flex;gap:8px;align-items:center;justify-self:end}
        .pol-row .btn-edit{white-space:nowrap}
        .pol-thumb{position:relative;width:42px;height:42px;border-radius:8px;display:flex;align-items:center;justify-content:center;background:#f4f5f9;font-size:1.1rem;color:#2a0516}
        .pol-thumb.is-clickable{cursor:pointer}
        .pol-thumb-img{width:100%;height:100%;object-fit:cover;border-radius:inherit}

        .pol-inline{grid-column:1 / -1;margin-top:12px}
        .thumb-badge{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);background:rgba(0,0,0,.65);color:#fff;font-size:10px;padding:3px 6px;border-radius:999px;pointer-events:none;white-space:nowrap}
        @media(max-width:700px){
          .pol-row{grid-template-columns:auto 1fr auto;gap:10px;padding:10px}
          .pol-row .pol-date-label{font-size:0.88rem}
          .pol-row .btn-edit{padding:8px 14px;font-size:0.85rem}
          .pol-thumb{width:44px;height:44px;border-radius:10px}
        }

        .pol-meta-block{display:grid;gap:4px;margin-bottom:12px;padding:10px;border-radius:10px;background:#f7f8fb;color:#2a0516;font-size:0.9rem}
        .pol-note-block{display:grid;gap:4px;padding:12px;border-radius:10px;background:#f7f8fb;color:#2a0516;font-size:0.95rem}
        .pol-note-heading{font-weight:600}
        .pol-note-body{white-space:pre-wrap;overflow-wrap:anywhere}
        .pol-sum{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:6px}
        @media(max-width:560px){ .pol-sum{grid-template-columns:1fr} }
      `}</style>
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} feature="documents" />
    </div>
  );
}
